import { useEffect } from 'react';
import { useMatch } from '@tanstack/react-location';
import { isBoolean } from 'lodash-es';
import { FilterGroupHeader, FilterGroupSection } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { RadioField, useFormField, DatePickerField } from '@frontend/design-system';
import { useReviewsFilterShallowStore } from '../../../hooks';

export const ReviewsFilterMenu = () => {
  const { t } = useTranslation('reviews');
  const {
    readFilter,
    repliedFilter,
    sortByNewest,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setReadFilter,
    setRepliedFilter,
    setSortByNewest,
    canClearFilters,
    resetAllExceptSourceFilter,
  } = useReviewsFilterShallowStore(
    'readFilter',
    'repliedFilter',
    'sortByNewest',
    'startDate',
    'endDate',
    'setStartDate',
    'setEndDate',
    'setReadFilter',
    'setRepliedFilter',
    'setSortByNewest',
    'canClearFilters',
    'resetAllExceptSourceFilter'
  );

  const { params } = useMatch();
  const startDateFieldProps = useFormField({ type: 'datePicker', value: startDate.toString() }, [startDate]);
  const endDateFieldProps = useFormField({ type: 'datePicker', value: endDate.toString() }, [endDate]);
  const sortFieldProps = useFormField({ type: 'radio', value: sortByNewest.toString() }, [sortByNewest]);
  const readFilterFieldProps = useFormField({ type: 'radio', value: `${readFilter}` }, [readFilter]);
  const repliedFilterFieldProps = useFormField({ type: 'radio', value: `${repliedFilter}` }, [repliedFilter]);

  useEffect(() => {
    if (`${readFilter}` !== readFilterFieldProps.value.toString()) {
      setReadFilter(readFilterFieldProps.value === 'true');
    }
  }, [readFilterFieldProps.value]);

  useEffect(() => {
    if (startDate !== startDateFieldProps.value) {
      setStartDate(startDateFieldProps.value);
    }
  }, [startDateFieldProps.value]);

  useEffect(() => {
    if (endDate !== endDateFieldProps.value) {
      setEndDate(endDateFieldProps.value);
    }
  }, [endDateFieldProps.value]);

  useEffect(() => {
    if (`${repliedFilter}` !== repliedFilterFieldProps.value.toString()) {
      setRepliedFilter(repliedFilterFieldProps.value === 'true');
    }
  }, [repliedFilterFieldProps.value]);

  useEffect(() => {
    if (sortByNewest.toString() !== sortFieldProps.value) {
      setSortByNewest(!sortByNewest);
    }
  }, [sortFieldProps.value]);

  return (
    <>
      <FilterGroupSection>
        <FilterGroupHeader
          headerText={t('Filter Reviews')}
          onClick={() => resetAllExceptSourceFilter()}
          onClickText={t('Clear All')}
          disabled={!canClearFilters()}
        />
      </FilterGroupSection>
      <FilterGroupSection addBottomPadding>
        <FilterGroupHeader
          onClick={() => {
            setStartDate('');
          }}
          onClickText={t('Clear')}
          disabled={!startDate}
        />
        <DatePickerField name='Start Date' label={t('Start Date')} {...startDateFieldProps} />
      </FilterGroupSection>
      <FilterGroupSection addBottomPadding>
        <FilterGroupHeader
          onClick={() => {
            setEndDate('');
          }}
          onClickText={t('Clear')}
          disabled={!endDate}
        />
        <DatePickerField name='end-date' label={t('End Date')} {...endDateFieldProps} />
      </FilterGroupSection>
      <FilterGroupSection>
        <FilterGroupHeader
          headerText={t('Read Status')}
          onClick={() => setReadFilter(undefined)}
          onClickText={t('Clear')}
          disabled={!isBoolean(readFilter)}
        />
        <RadioField {...readFilterFieldProps} name='replied-filter'>
          <RadioField.Radio value={'true'}>{t(`Read`)}</RadioField.Radio>
          <RadioField.Radio value={'false'}>{t(`Unread`)}</RadioField.Radio>
        </RadioField>
      </FilterGroupSection>

      {params.site !== 'private' && (
        <FilterGroupSection>
          <FilterGroupHeader
            headerText={t('Reply Status')}
            onClick={() => setRepliedFilter(undefined)}
            onClickText={t('Clear')}
            disabled={!isBoolean(repliedFilter)}
          />
          <RadioField {...repliedFilterFieldProps} name='replied-filter'>
            <RadioField.Radio value={'true'}>{t(`Replied`)}</RadioField.Radio>
            <RadioField.Radio value={'false'}>{t(`Haven't Replied`)}</RadioField.Radio>
          </RadioField>
        </FilterGroupSection>
      )}

      <FilterGroupSection hideBorderBottom>
        <FilterGroupHeader headerText={t('Sort')} />
        <RadioField {...sortFieldProps} name='source-filter'>
          <RadioField.Radio value={'true'}>{t(`Newest`)}</RadioField.Radio>
          <RadioField.Radio value={'false'}>{t(`Oldest`)}</RadioField.Radio>
        </RadioField>
      </FilterGroupSection>
    </>
  );
};
